/**
 * Send Scope reference data — the seven Step-3 fields the production monolith
 * collects alongside the audience builder (index.html, Step 3 markup ~95.5k;
 * option arrays SENDER_TYPE / UTM_TARGET / AUD_TYPE ~4060-4130).
 *
 * These are NOT an alternative to the Seg3 builder — the monolith renders both.
 * The builder decides WHO the email goes to; these decide how the send is
 * classified for UTM 2026, which Salesforce lists ride along, and who it comes
 * from. The monolith keeps two of them in step with the builder via its
 * `syncLegacy()` (persona -> UTM Targeting, audience code -> Email Audience
 * Type); SendScope.tsx reproduces that, and a manual edit still wins.
 */

/** Sender Type (monolith SENDER_TYPE). What the "from" represents. */
export const SENDER_TYPE: string[] = [
  "Market", "MarketSpanish", "Masterplan", "Community", "City",
];
export const SENDER_TYPE_DEFAULT = "Market";

/** UTM Targeting (monolith UTM_TARGET). `persona` maps to the Seg3 persona key. */
export const UTM_TARGET: { v: string; persona?: string }[] = [
  { v: "Market" },
  { v: "Community" },
  { v: "ActiveAdult", persona: "ActiveAdult" },
  { v: "FTHB", persona: "FTHB" },
  { v: "MoveUp", persona: "MoveUp" },
  { v: "National", persona: "National" },
  { v: "Investor", persona: "Investor" },
];
export const UTM_TARGET_DEFAULT = "FTHB";

/** Seg3 persona -> UTM Targeting value (monolith personaKey, inverted). */
export function utmTargetForPersona(persona?: string): string | undefined {
  if (!persona) return undefined;
  return UTM_TARGET.find((u) => u.persona === persona)?.v;
}

/** Email Audience Type (monolith AUD_TYPE): [code, label]. Default LO. */
export const AUD_TYPE: [string, string][] = [
  ["LO", "LO = Leads & Opportunities"],
  ["H", "H = Homeowners"],
  ["B", "B = Buyers"],
  ["HB", "HB = Homeowners & Buyers"],
  ["L", "L = Leads"],
  ["O", "O = Opportunities"],
  ["R", "R = Real Estate Professional"],
  ["LOR", "LOR = Leads, Opps & Real Estate Pro"],
  ["A", "A = Associates"],
];
export const AUD_TYPE_DEFAULT = "LO";
export const isAudTypeCode = (c?: string) => !!c && AUD_TYPE.some(([v]) => v === c);

/** info-tooltip copy, VERBATIM from the monolith's data-tip attributes.
 *
 *  Re-aligned 2026-08-19 (Katy Stenerson: "Trying to keep consistent with the
 *  current email form … the field naming/helper text"). Six of these had drifted
 *  into repo-written wording that described the React app's internals rather than
 *  telling the requester what to type; each is now the exact string the live form
 *  shows (index.html L1001–1011). Only `utmTarget` already matched. The
 *  before/after table is in
 *  Projects/Email/Smart Email Request/_changesets/2026-08-19-katy-form-changes.md
 *  — do not "improve" these again without a matching change to the live form. */
export const SCOPE_TIP = {
  senderType:
    "Going to a specific community, masterplan, cross-sell, etc.?",
  utmTarget:
    "Targeting a specific buyer profile? Drives utm_term and the audience recommendation.",
  audType:
    "In most cases, send to Leads and Opportunities (LO).",
  sfInclude:
    "Report List names to include. Add the sender profile name if applicable.",
  sfExclude:
    "Report List names to exclude, if necessary.",
  senderProfile:
    "Sender Profile email, if applicable — mainly for Divisions with multiple markets. Example: Lennar Seattle, Lennar Charleston.",
  pilot:
    "Only select if you have been chosen as a participant in an email test.",
};
